import { squashAtomCommandFailure } from "@t3tools/client-runtime/state/runtime";
import type {
  EnvironmentId,
  ThreadId,
  VmControlStatus,
  VmDesktopInputAction,
  VmStatus,
} from "@t3tools/contracts";
import * as Option from "effect/Option";
import { useCallback, useEffect, useRef, useState } from "react";
import { AsyncResult } from "effect/unstable/reactivity";

import { vmEnvironment } from "~/state/vm";
import { usePreparedConnection } from "~/state/session";
import { useAtomCommand } from "~/state/use-atom-command";

import {
  RemoteDesktopSurface,
  type RemoteDesktopControl,
  type RemoteDesktopInput,
  type RemoteDesktopStatus,
} from "./RemoteDesktopSurface";
import { VmDesktopRfb } from "./VmDesktopRfb";
import { resolveVmDesktopStream } from "./vmDesktopStream";

const FRAME_INTERVAL_MS = 1_000;
const CONTROL_HEARTBEAT_INTERVAL_MS = 10_000;
const RFB_RETRY_DELAYS_MS = [250, 500] as const;
const RFB_STABILITY_WINDOW_MS = 5_000;

type VmDesktopStream = ReturnType<typeof resolveVmDesktopStream> & {
  readonly password: string;
};

function commandError(result: Parameters<typeof squashAtomCommandFailure>[0]): Error {
  const cause = squashAtomCommandFailure(result);
  if (cause instanceof Error) return cause;
  if (typeof cause === "object" && cause !== null && "message" in cause) {
    const message = (cause as { readonly message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) return new Error(message);
  }
  return new Error("VM desktop request failed.");
}

function remoteStatus(
  status: VmStatus | null,
  fallbackReason: string | null,
): RemoteDesktopStatus | null {
  if (!status) return null;
  switch (status.state) {
    case "running":
      return fallbackReason
        ? { state: "running", label: "Compatibility mode", message: fallbackReason }
        : { state: "running", label: "VM online" };
    case "creating":
    case "starting":
      return { state: "starting", label: "Starting VM" };
    case "missing":
      return {
        state: "stopped",
        label: "VM not created",
        message: "Choose VM and send the first message to create this environment.",
      };
    case "stopped":
      return { state: "stopped", label: "VM stopped" };
    case "error":
      return {
        state: "error",
        label: "VM error",
        ...(status.error ? { message: status.error } : {}),
      };
    case "unavailable":
      return {
        state: "unavailable",
        label: "VM host unavailable",
        message: status.error ?? "Start the Apple Container worker on the host.",
      };
  }
}

function remoteControl(
  status: VmStatus | null,
  control: VmControlStatus | null,
): RemoteDesktopControl {
  const available = status?.state === "running" && status.desktopRunning;
  if (!control) {
    return {
      available,
      heldByCurrentSession: false,
      controller: available ? "agent" : "none",
      canTakeControl: available,
    };
  }
  return {
    available,
    heldByCurrentSession: control.heldByCurrentConnection,
    controller:
      control.mode === "agent" ? "agent" : control.heldByCurrentConnection ? "human" : "other",
    canTakeControl: available && (control.mode === "agent" || control.heldByCurrentConnection),
  };
}

function vmInputAction(action: RemoteDesktopInput): VmDesktopInputAction {
  switch (action.type) {
    case "key": {
      const modifiers = action.modifiers?.map((modifier) => {
        switch (modifier) {
          case "Alt":
            return "alt" as const;
          case "Control":
            return "ctrl" as const;
          case "Meta":
            return "meta" as const;
          case "Shift":
            return "shift" as const;
        }
      });
      return {
        type: "key",
        event: action.event,
        key: action.key,
        ...(modifiers && modifiers.length > 0 ? { modifiers } : {}),
      };
    }
    case "scroll":
      return {
        type: "scroll",
        deltaX: Math.max(-4096, Math.min(4096, Math.trunc(action.deltaX))),
        deltaY: Math.max(-4096, Math.min(4096, Math.trunc(action.deltaY))),
        ...(action.x === undefined ? {} : { x: action.x }),
        ...(action.y === undefined ? {} : { y: action.y }),
      };
    default:
      return action;
  }
}

function retryError(error: Error): string {
  return error.message.trim().length > 0
    ? `RFB unavailable; using compatibility mode. ${error.message}`
    : "RFB unavailable; using compatibility mode.";
}

export function VmDesktopPanel(props: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
}) {
  const getStatus = useAtomCommand(vmEnvironment.status, { reportFailure: false });
  const resumeVmCommand = useAtomCommand(vmEnvironment.resume, { reportFailure: false });
  const getFrame = useAtomCommand(vmEnvironment.frame, { reportFailure: false });
  const openDesktop = useAtomCommand(vmEnvironment.desktopOpen, { reportFailure: false });
  const getControlStatus = useAtomCommand(vmEnvironment.controlStatus, { reportFailure: false });
  const takeControlCommand = useAtomCommand(vmEnvironment.takeControl, { reportFailure: false });
  const heartbeatControl = useAtomCommand(vmEnvironment.heartbeatControl, {
    reportFailure: false,
  });
  const returnControlCommand = useAtomCommand(vmEnvironment.returnControl, {
    reportFailure: false,
  });
  const sendInput = useAtomCommand(vmEnvironment.input, { reportFailure: false });
  const preparedConnection = usePreparedConnection(props.environmentId);
  const connection = Option.getOrNull(preparedConnection);
  const requestKey = `${props.environmentId}\n${props.threadId}`;
  const connectionKey =
    connection === null ? null : `${connection.httpBaseUrl}\n${connection.socketUrl}`;
  const [status, setStatus] = useState<VmStatus | null>(null);
  const [control, setControl] = useState<VmControlStatus | null>(null);
  const [frameDataUrl, setFrameDataUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<VmDesktopStream | null>(null);
  const [rfbConnected, setRfbConnected] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const activeRequestKeyRef = useRef(requestKey);
  const heldControlRef = useRef(false);
  const inputQueueRef = useRef(Promise.resolve());
  const streamConnectionKeyRef = useRef<string | null>(null);
  const activeConnectionKeyRef = useRef(connectionKey);
  const activeConnectionRef = useRef(connection);
  const rfbConnectedRef = useRef(false);
  const openingRef = useRef(false);
  const openingConnectionKeyRef = useRef<string | null>(null);
  const admissionGenerationRef = useRef(0);
  const refreshGenerationRef = useRef(0);
  const streamFailureCountRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const rfbStabilityTimerRef = useRef<number | null>(null);
  const previousConnectionRef = useRef(connection);
  activeRequestKeyRef.current = requestKey;
  activeConnectionKeyRef.current = connectionKey;
  activeConnectionRef.current = connection;

  const request = useCallback(
    () => ({ environmentId: props.environmentId, input: { threadId: props.threadId } }),
    [props.environmentId, props.threadId],
  );

  const setStreamValue = useCallback((next: VmDesktopStream | null) => {
    setStream(next);
  }, []);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const clearRfbStabilityTimer = useCallback(() => {
    if (rfbStabilityTimerRef.current !== null) {
      window.clearTimeout(rfbStabilityTimerRef.current);
      rfbStabilityTimerRef.current = null;
    }
  }, []);

  const clearStream = useCallback(() => {
    clearRetryTimer();
    clearRfbStabilityTimer();
    admissionGenerationRef.current += 1;
    refreshGenerationRef.current += 1;
    openingRef.current = false;
    openingConnectionKeyRef.current = null;
    setStreamValue(null);
    streamConnectionKeyRef.current = null;
    rfbConnectedRef.current = false;
    setRfbConnected(false);
  }, [clearRetryTimer, clearRfbStabilityTimer, setStreamValue]);

  const refreshState = useCallback(
    async (options?: { readonly forceFrame?: boolean }) => {
      const requestKeyAtStart = requestKey;
      const connectionAtStart = connection;
      const refreshGeneration = ++refreshGenerationRef.current;
      const isCurrent = () =>
        mountedRef.current &&
        activeRequestKeyRef.current === requestKeyAtStart &&
        activeConnectionRef.current === connectionAtStart &&
        refreshGenerationRef.current === refreshGeneration;
      const statusResult = await getStatus(request());
      if (!AsyncResult.isSuccess(statusResult)) throw commandError(statusResult);
      if (!isCurrent()) return;
      const nextStatus = statusResult.value;
      setStatus(nextStatus);
      if (nextStatus.state !== "running" || !nextStatus.desktopRunning) {
        clearStream();
        setFallbackReason(null);
        setFrameDataUrl(null);
        setControl(null);
        return;
      }

      const shouldFetchFrame = options?.forceFrame === true || !rfbConnectedRef.current;
      const [frameResult, controlResult] = await Promise.all([
        shouldFetchFrame ? getFrame(request()) : Promise.resolve(null),
        getControlStatus(request()),
      ]);
      if (!isCurrent()) return;
      if (frameResult !== null && AsyncResult.isSuccess(frameResult)) {
        setFrameDataUrl(
          `data:${frameResult.value.mimeType};base64,${frameResult.value.dataBase64}`,
        );
      }
      if (AsyncResult.isSuccess(controlResult)) {
        heldControlRef.current = controlResult.value.heldByCurrentConnection;
        setControl(controlResult.value);
      }
    },
    [clearStream, connection, getControlStatus, getFrame, getStatus, request, requestKey],
  );

  const fallBack = useCallback(
    (error: Error) => {
      if (!mountedRef.current) return;
      clearStream();
      setFallbackReason(retryError(error));
    },
    [clearStream],
  );

  const openStream = useCallback(async () => {
    if (
      !mountedRef.current ||
      connection === null ||
      openingRef.current ||
      fallbackReason !== null ||
      rfbConnectedRef.current
    ) {
      return;
    }
    const admissionGeneration = admissionGenerationRef.current + 1;
    const connectionAtStart = connection;
    admissionGenerationRef.current = admissionGeneration;
    openingRef.current = true;
    openingConnectionKeyRef.current = connectionKey;
    try {
      const result = await openDesktop(request());
      if (
        !mountedRef.current ||
        admissionGenerationRef.current !== admissionGeneration ||
        activeConnectionKeyRef.current !== connectionKey ||
        activeConnectionRef.current !== connectionAtStart ||
        activeRequestKeyRef.current !== requestKey
      ) {
        return;
      }
      if (!AsyncResult.isSuccess(result)) {
        fallBack(commandError(result));
        return;
      }
      const resolved = resolveVmDesktopStream({
        connection,
        environmentId: props.environmentId,
        threadId: props.threadId,
        opened: result.value,
      });
      const next: VmDesktopStream = { ...resolved, password: result.value.password };
      streamConnectionKeyRef.current = connectionKey;
      setFallbackReason(null);
      setStreamValue(next);
    } catch (error) {
      if (
        mountedRef.current &&
        admissionGenerationRef.current === admissionGeneration &&
        activeConnectionKeyRef.current === connectionKey &&
        activeConnectionRef.current === connectionAtStart &&
        activeRequestKeyRef.current === requestKey
      ) {
        fallBack(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      if (admissionGenerationRef.current === admissionGeneration) {
        openingRef.current = false;
        openingConnectionKeyRef.current = null;
      }
    }
  }, [
    connection,
    connectionKey,
    fallBack,
    fallbackReason,
    openDesktop,
    props.environmentId,
    props.threadId,
    request,
    requestKey,
    setStreamValue,
  ]);

  useEffect(() => {
    const previousConnection = previousConnectionRef.current;
    previousConnectionRef.current = connection;
    if (previousConnection === connection) return;

    // A connection can disappear and reappear with the same URL. Treat its
    // object identity as a new admission epoch so stale fallback state,
    // frames, tickets, and retry timers cannot suppress the new session.
    streamFailureCountRef.current = 0;
    clearStream();
    setFallbackReason(null);
    setFrameDataUrl(null);
    setControl(null);
  }, [clearStream, connection]);

  useEffect(() => {
    if (
      openingRef.current &&
      openingConnectionKeyRef.current !== null &&
      openingConnectionKeyRef.current !== connectionKey
    ) {
      clearStream();
      return;
    }
    if (
      streamConnectionKeyRef.current === null ||
      streamConnectionKeyRef.current === connectionKey
    ) {
      return;
    }
    clearStream();
    streamFailureCountRef.current = 0;
    setFallbackReason(null);
    setFrameDataUrl(null);
  }, [clearStream, connectionKey]);

  useEffect(() => {
    if (
      status?.state !== "running" ||
      !status.desktopRunning ||
      connection === null ||
      fallbackReason !== null ||
      stream !== null ||
      streamConnectionKeyRef.current !== null
    ) {
      return;
    }
    void openStream();
  }, [connection, fallbackReason, openStream, status?.desktopRunning, status?.state, stream]);

  const handleRfbConnect = useCallback(() => {
    clearRfbStabilityTimer();
    rfbConnectedRef.current = true;
    setRfbConnected(true);
    setFallbackReason(null);
    rfbStabilityTimerRef.current = window.setTimeout(() => {
      rfbStabilityTimerRef.current = null;
      if (mountedRef.current && rfbConnectedRef.current) {
        streamFailureCountRef.current = 0;
      }
    }, RFB_STABILITY_WINDOW_MS);
  }, [clearRfbStabilityTimer]);

  const handleRfbDisconnect = useCallback(
    (error?: Error) => {
      if (!mountedRef.current) return;
      clearRfbStabilityTimer();
      rfbConnectedRef.current = false;
      setRfbConnected(false);
      setStreamValue(null);
      const disconnectError = error ?? new Error("The VM desktop stream disconnected.");
      streamFailureCountRef.current += 1;
      const retryIndex = streamFailureCountRef.current - 1;
      if (retryIndex >= RFB_RETRY_DELAYS_MS.length) {
        fallBack(disconnectError);
        void refreshState({ forceFrame: true }).catch(() => undefined);
        return;
      }
      setFallbackReason("Reconnecting to the VM desktop…");
      clearRetryTimer();
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        if (!mountedRef.current) return;
        setFallbackReason(null);
        streamConnectionKeyRef.current = null;
      }, RFB_RETRY_DELAYS_MS[retryIndex]);
      void refreshState({ forceFrame: true }).catch(() => undefined);
    },
    [clearRfbStabilityTimer, clearRetryTimer, fallBack, refreshState, setStreamValue],
  );

  const refresh = useCallback(async () => {
    streamFailureCountRef.current = 0;
    setFallbackReason(null);
    clearStream();
    await refreshState({ forceFrame: true });
  }, [clearStream, refreshState]);

  const resumeVm = useCallback(async () => {
    const result = await resumeVmCommand({
      environmentId: props.environmentId,
      input: {
        threadId: props.threadId,
      },
    });
    if (!AsyncResult.isSuccess(result)) throw commandError(result);
    if (mountedRef.current) setStatus(result.value);
    await refreshState({ forceFrame: true });
  }, [props.environmentId, props.threadId, refreshState, resumeVmCommand]);

  useEffect(() => {
    mountedRef.current = true;
    let timeout: number | null = null;
    const poll = async () => {
      try {
        await refreshState();
      } catch {
        // vm.status converts an unavailable worker into a typed status. Other
        // transient failures keep the last good frame until the next poll.
      }
      if (mountedRef.current) {
        timeout = window.setTimeout(() => void poll(), FRAME_INTERVAL_MS);
      }
    };
    void poll();
    return () => {
      mountedRef.current = false;
      if (timeout !== null) window.clearTimeout(timeout);
      clearStream();
      clearRfbStabilityTimer();
      setStatus(null);
      setControl(null);
      setFrameDataUrl(null);
      setFallbackReason(null);
      streamFailureCountRef.current = 0;
      if (heldControlRef.current) {
        void returnControlCommand(request());
        heldControlRef.current = false;
      }
    };
  }, [clearRfbStabilityTimer, clearStream, refreshState, request, returnControlCommand]);

  useEffect(() => {
    if (!control?.heldByCurrentConnection) return;
    const interval = window.setInterval(() => {
      void heartbeatControl(request()).then((result) => {
        if (!mountedRef.current || !AsyncResult.isSuccess(result)) return;
        heldControlRef.current = result.value.heldByCurrentConnection;
        setControl(result.value);
      });
    }, CONTROL_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [control?.heldByCurrentConnection, heartbeatControl, request]);

  const takeControl = useCallback(async () => {
    const result = await takeControlCommand(request());
    if (!AsyncResult.isSuccess(result)) throw commandError(result);
    heldControlRef.current = result.value.heldByCurrentConnection;
    setControl(result.value);
  }, [request, takeControlCommand]);

  const returnControl = useCallback(async () => {
    const result = await returnControlCommand(request());
    if (!AsyncResult.isSuccess(result)) throw commandError(result);
    heldControlRef.current = false;
    setControl(result.value);
  }, [request, returnControlCommand]);

  const input = useCallback(
    (action: RemoteDesktopInput) => {
      if (rfbConnectedRef.current) return Promise.resolve();
      const pending = inputQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          if (rfbConnectedRef.current) return;
          const result = await sendInput({
            environmentId: props.environmentId,
            input: { threadId: props.threadId, action: vmInputAction(action) },
          });
          if (!AsyncResult.isSuccess(result)) throw commandError(result);
        });
      inputQueueRef.current = pending.catch(() => undefined);
      return pending;
    },
    [props.environmentId, props.threadId, sendInput],
  );

  const rfb = stream ? (
    <VmDesktopRfb
      url={stream.url}
      protocols={stream.protocols}
      password={stream.password}
      viewOnly={!control?.heldByCurrentConnection}
      onConnect={handleRfbConnect}
      onDisconnect={handleRfbDisconnect}
    />
  ) : null;

  return (
    <RemoteDesktopSurface
      frameDataUrl={frameDataUrl}
      rfb={rfb}
      rfbConnected={rfbConnected}
      status={remoteStatus(status, fallbackReason)}
      control={remoteControl(status, control)}
      {...(status?.state === "stopped" ? { resume: resumeVm } : {})}
      takeControl={takeControl}
      returnControl={returnControl}
      refresh={refresh}
      input={input}
    />
  );
}
